import React, { useCallback } from 'react';
import { Popover, makeStyles, Typography } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { useApolloClient } from '@apollo/client';
import { DimensionObjectType } from '~/types';
import {
  ProjectTeamMemberDropdown,
  RoleDropdown,
  ResourceRoleDropdown
} from '~/modules/tasks/components';
import { User } from '~/modules/common/components/User';
import { EVENT_TYPE } from '~/modules/common/enums/EventTypes';
import { useIsRateTableDimensionEnabled } from '~/modules/rateCard/advancedRateCard/components/hooks';
import { useTaskEstimateRowContext } from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/TaskEstimateRowContext';
import { useHasFeatureFlag, useSessionStorage } from '~/modules/common/hooks';
import useOnChangeHandlers from '../../../hooks/useOnChangeHandlers';
import { updateTaskResourceEstimateEntryInTaskResourceEstimatesListQueryCache as updateTaskResourceEstimateEntry } from '../../../hooks/useTaskResourceEstimates';

const useStyles = makeStyles(theme => ({
  roleDropdown: {
    padding: theme.spacing(0, 1, 0, 0)
  },
  resourceDropdown: {
    padding: theme.spacing(0, 0, 0, 1),
    width: '50%'
  },
  resource: {
    padding: theme.spacing(0, 0, 0, 1),
    margin: theme.spacing(-1.25, 0, 0),
    width: '50%'
  },
  resourceLabel: {
    color: theme.palette.text.secondary
  }
}));

const usePopoverStyles = makeStyles(theme => ({
  paper: {
    maxWidth: theme.spacing(65),
    width: theme.spacing(65),
    padding: theme.spacing(2, 1.5, 1.5),
    display: 'flex'
  }
}));

const useUserStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(0)
  },
  name: {
    fontSize: theme.typography.body1.fontSize
  }
}));

const useRoleDropdownStyles = makeStyles(theme => ({
  root: {
    width: '50%'
  }
}));

const useRateTableRoleDropdowntyles = makeStyles(theme => ({
  roleContainer: {
    width: '50%',
    padding: theme.spacing(0, 1, 0, 0)
  }
}));

const ResourceRoleDropdownEditor = ({
  anchorEl,
  setAnchorEl,
  resourceEstimate,
  rowIndex
}) => {
  const classes = useStyles();
  const popoverClasses = usePopoverStyles();
  const { formatMessage } = useIntl();
  const userClasses = useUserStyles();
  const roleDropdownClasses = useRoleDropdownStyles();
  const rateTableRoleDropdownClasses = useRateTableRoleDropdowntyles();
  const { task } = useTaskEstimateRowContext();
  const apollClient = useApolloClient();
  const { storedValue: variables } = useSessionStorage(
    'get-task-resource-estimates-for-task-variables'
  );
  const isPsaPrpRoleFilterOnTasksEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpRoleFilterOnTasksEnabled'
  });

  const {
    id: taskId,
    projectReference: { id: projectId }
  } = task;

  const {
    loading: rateTableDimensionLoading,
    isDimensionEnabled: isRoleRateTableDimensionEnabled
  } = useIsRateTableDimensionEnabled({
    projectId,
    dimensionType: DimensionObjectType.ProjectRole
  });

  const {
    resourceUser,
    projectRole,
    id,
    taskResourceUserAllocation
  } = resourceEstimate;

  const { setFieldValue } = useFormikContext();

  const { onRoleChange, onResourceChange } = useOnChangeHandlers({
    resourceEstimate,
    setFieldValue,
    rowIndex
  });

  const handleOnRoleChange = useCallback(
    async role => {
      if (isPsaPrpRoleFilterOnTasksEnabled) {
        await onRoleChange(role?.id);
        setFieldValue(`resourceEstimates[${rowIndex}].projectRole`, role);

        updateTaskResourceEstimateEntry(
          apollClient,
          variables
        )({
          id,
          projectRole: role?.id
            ? {
                id: role.id,
                displayText: role.displayText,
                __typename: 'ProjectRole'
              }
            : null,
          taskResourceUserAllocation: taskResourceUserAllocation
            ? {
                ...taskResourceUserAllocation,
                roleUri: role?.id || null
              }
            : undefined
        });
      } else {
        onRoleChange(role?.id);
        setFieldValue(`resourceEstimates[${rowIndex}].projectRole`, role);
      }
    },
    [
      isPsaPrpRoleFilterOnTasksEnabled,
      onRoleChange,
      setFieldValue,
      rowIndex,
      apollClient,
      variables,
      id,
      taskResourceUserAllocation
    ]
  );

  const handleOnResourceChange = useCallback(
    resource => {
      onResourceChange(resource?.id, [
        {
          eventName: EVENT_TYPE.GANTT_TASK_RESOURCE_COUNT_UPDATE,
          payload: {
            taskId,
            updateResourceEstimatesCountBy: 0,
            updateResourceEstimatesWithUsersCountBy: 1
          }
        }
      ]);
    },
    [onResourceChange, taskId]
  );

  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const addResourceLabel = formatMessage({
    id: 'taskResourceEstimates.resource'
  });

  const addRoleLabel = formatMessage({
    id: 'taskResourceEstimates.role'
  });

  return (
    <Popover
      id="resource-role-editor"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      classes={popoverClasses}
    >
      {isPsaPrpRoleFilterOnTasksEnabled &&
      (rateTableDimensionLoading || isRoleRateTableDimensionEnabled) ? (
        <ResourceRoleDropdown
          classes={rateTableRoleDropdownClasses}
          variant="outlined"
          placeholder={addRoleLabel}
          label={addRoleLabel}
          value={projectRole}
          onChange={handleOnRoleChange}
          disableClearable={!resourceUser?.id}
          projectId={projectId}
          rateTableDimensionLoading={rateTableDimensionLoading}
        />
      ) : (
        <RoleDropdown
          className={classes.roleDropdown}
          classes={roleDropdownClasses}
          variant="outlined"
          placeholder={addRoleLabel}
          label={addRoleLabel}
          value={projectRole}
          onChange={handleOnRoleChange}
          disableClearable={!resourceUser?.id}
        />
      )}
      {resourceUser ? (
        <div className={classes.resource}>
          <Typography variant="caption" className={classes.resourceLabel}>
            {addResourceLabel}
          </Typography>
          <User classes={userClasses} user={resourceUser} />
        </div>
      ) : (
        <ProjectTeamMemberDropdown
          className={classes.resourceDropdown}
          variant="outlined"
          label={addResourceLabel}
          ariaLabel={addResourceLabel}
          placeholder={addResourceLabel}
          projectId={projectId}
          assignedRole={projectRole}
          value={resourceUser}
          onResourceChange={handleOnResourceChange}
          taskId={taskId}
        />
      )}
    </Popover>
  );
};

ResourceRoleDropdownEditor.propTypes = {
  anchorEl: PropTypes.object,
  setAnchorEl: PropTypes.func,
  resourceEstimate: PropTypes.object,
  rowIndex: PropTypes.number
};

export default ResourceRoleDropdownEditor;
